// Stimulus controller for the workflow form and list.
// Handles:
//   - Adding/removing step rows dynamically
//   - Schedule type toggle (show/hide cron fields + task-schedule UI)
//   - Output config toggle (show/hide email/drive fields)
//   - Position counter for new steps
//   - Cron expression sync: reads scheduleData JSON from task-schedule controller
//     and converts it to EventBridge cron format in the hidden cron_expression field

import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import Tags from "bootstrap5-tags"

// Day-of-week map: task-schedule day name → EventBridge cron abbreviation
const DOW_MAP = {
  sunday:    "SUN",
  monday:    "MON",
  tuesday:   "TUE",
  wednesday: "WED",
  thursday:  "THU",
  friday:    "FRI",
  saturday:  "SAT",
}

export default class extends Controller {
  static targets = [
    "stepsContainer",
    "scheduleType",
    "cronFields",
    "cronExpression",
    "scheduleData",
    "cronPreview",
    "taskScheduleContainer",
    "emailConfig",
    "driveConfig",
    "stepRow",
    "stepTemplate",
    "stepPosition",
    "stepDestroy",
    "recipientsInput",
    "recipientPeopleSelect",
    "recipientGroupsSelect",
    "recipientRolesSelect",
    "recipientsHiddenContainer",
    "driveFolderDisplay",
    "driveFolderIdInput",
    "folderPickerList",
    "folderPickerNav",
    "tenantSelectWrapper",
    "globalTenantHint",
  ]

  static values = {
    folderPickerBackLabel:     String,
    folderPickerOpenLabel:     String,
    folderPickerRootLabel:     String,
    folderPickerNoFoldersText: String,
    folderPickerErrorText:     String,
    folderPickerSelectLabel:   String,
  }

  connect() {
    this._stepIndex = this.stepRowTargets.length
    this.stepRowTargets.forEach((row, i) => {
      const posField = row.querySelector("[data-workflow-target~='stepPosition']")
      if (posField) posField.value = i
    })

    // Navigation stack for the folder picker: array of { id, name } objects.
    // Empty array = root level.
    this._folderNavStack = []

    // Initialize bootstrap5-tags on the person and group autocomplete selects.
    if (this.hasRecipientPeopleSelectTarget) {
      Tags.init("#" + this.recipientPeopleSelectTarget.id, {
        allowClear: true,
        server: this.recipientPeopleSelectTarget.dataset.server,
        liveServer: true,
        allowNew: false,
        labelField: "name",
        valueField: "id",
        searchFields: ["name"],
      })
    }

    if (this.hasRecipientGroupsSelectTarget) {
      Tags.init("#" + this.recipientGroupsSelectTarget.id, {
        allowClear: true,
        server: this.recipientGroupsSelectTarget.dataset.server,
        liveServer: true,
        allowNew: false,
        labelField: "name",
        valueField: "id",
        searchFields: ["name"],
      })
    }

    // Intercept value writes to the scheduleData hidden input so that task-schedule
    // controller updates (which assign via JS property, not user input) are captured.
    if (this.hasScheduleDataTarget) {
      this._interceptScheduleDataWrites(this.scheduleDataTarget)

      // If the field already carries a pre-populated JSON (edit form with existing cron),
      // sync the cron preview immediately after task-schedule has had a chance to
      // initialize its own UI (next microtask tick is enough).
      const initialValue = this.scheduleDataTarget.value
      if (initialValue && initialValue !== "{}" && initialValue !== "null") {
        setTimeout(() => this.syncCronExpression(), 0)
      }
    }

    // Reflect initial state of the global_tenant checkbox on page load
    const globalCheckbox = this.element.querySelector("input[name*='global_tenant']")
    if (globalCheckbox) this._applyGlobalTenantState(globalCheckbox.checked)
  }

  toggleGlobalTenant(event) {
    this._applyGlobalTenantState(event.target.checked)
  }

  _applyGlobalTenantState(isGlobal) {
    if (this.hasTenantSelectWrapperTarget) {
      this.tenantSelectWrapperTarget.style.opacity = isGlobal ? "0.4" : ""
      const select = this.tenantSelectWrapperTarget.querySelector("select")
      if (select) select.disabled = isGlobal
    }
    if (this.hasGlobalTenantHintTarget) {
      this.globalTenantHintTarget.style.display = isGlobal ? "" : "none"
    }
  }

  // Monkey-patch the scheduleData input's value setter to fire syncCronExpression
  // whenever task-schedule controller writes JSON to it programmatically.
  _interceptScheduleDataWrites(input) {
    const proto     = Object.getPrototypeOf(input)
    const descriptor = Object.getOwnPropertyDescriptor(proto, "value")
    if (!descriptor) return

    const controller = this
    Object.defineProperty(input, "value", {
      get() { return descriptor.get.call(this) },
      set(v) {
        descriptor.set.call(this, v)
        controller.syncCronExpression()
      },
      configurable: true,
    })
  }

  // Toggle cron fields when schedule type changes
  toggleSchedule(event) {
    const isEventBridge = event.target.value === "eventbridge"
    if (this.hasCronFieldsTarget) {
      this.cronFieldsTarget.classList.toggle("visually-hidden", !isEventBridge)
    }
  }

  // Convert task-schedule JSON (scheduleData) to EventBridge cron and write to cron_expression.
  // Called via data-action="input->workflow#syncCronExpression" on the scheduleData hidden input.
  syncCronExpression() {
    if (!this.hasScheduleDataTarget || !this.hasCronExpressionTarget) return

    let data
    try {
      data = JSON.parse(this.scheduleDataTarget.value)
    } catch {
      return
    }
    if (!data || !data.frequency) return

    const cron = this._buildEventBridgeCron(data)
    if (!cron) return

    this.cronExpressionTarget.value = cron

    if (this.hasCronPreviewTarget) {
      this.cronPreviewTarget.textContent = cron
    }
  }

  // Build an EventBridge cron string from the task-schedule JSON schedule object.
  // Format: cron(minutes hours day-of-month month day-of-week year)
  _buildEventBridgeCron(data) {
    const { frequency, days, monthDay, month, times, endType } = data
    const freqType = frequency?.type || "week"
    const time     = this._parseTime(data)
    const [minute, hour] = time

    switch (freqType) {
      case "day": {
        const interval = frequency.value || 1
        // Every N days at time: day-of-month and day-of-week cannot both be specified
        // Use rate() equivalent: every day = cron(min hour * * ? *)
        if (interval === 1) {
          return `cron(${minute} ${hour} * * ? *)`
        }
        // EventBridge doesn't support "every N days" in cron, approximate with daily and note
        return `cron(${minute} ${hour} * * ? *)`
      }

      case "week": {
        if (!days || Object.keys(days).length === 0) {
          return `cron(${minute} ${hour} ? * MON *)`
        }
        const dow = Object.keys(days)
          .map(d => DOW_MAP[d])
          .filter(Boolean)
          .join(",")
        return `cron(${minute} ${hour} ? * ${dow || "MON"} *)`
      }

      case "month": {
        const day = monthDay || 1
        return `cron(${minute} ${hour} ${day} * ? *)`
      }

      case "year": {
        const day  = monthDay || 1
        const mon  = month   || 1
        return `cron(${minute} ${hour} ${day} ${mon} ? *)`
      }

      default:
        return null
    }
  }

  // Extract [minute, hour] from schedule data. Returns strings suitable for cron.
  _parseTime(data) {
    let timeStr = "09:00"

    if (data.frequency?.type === "week" && data.days) {
      for (const day in data.days) {
        if (Array.isArray(data.days[day]) && data.days[day].length > 0) {
          timeStr = data.days[day][0]
          break
        }
      }
    } else if (Array.isArray(data.times) && data.times.length > 0) {
      timeStr = data.times[0]
    }

    const [h, m] = (timeStr || "09:00").split(":").map(Number)
    return [m || 0, h || 9]
  }

  // Collect all recipient values from the three pickers and the free-text
  // textarea, inject them as hidden inputs under recipients[], then disable
  // the picker <select> elements so they don't submit their own values.
  mergeRecipients(event) {
    if (!this.hasRecipientsHiddenContainerTarget) return

    const container = this.recipientsHiddenContainerTarget
    container.innerHTML = ""

    const addHidden = (value) => {
      const input = document.createElement("input")
      input.type  = "hidden"
      input.name  = "ai_workflow[output_config][recipients][]"
      input.value = value
      container.appendChild(input)
    }

    // Person selections → "person:<id>"
    if (this.hasRecipientPeopleSelectTarget) {
      Array.from(this.recipientPeopleSelectTarget.selectedOptions).forEach(opt => {
        addHidden(`person:${opt.value}`)
      })
      this.recipientPeopleSelectTarget.disabled = true
    }

    // Group selections → "group:<id>"
    if (this.hasRecipientGroupsSelectTarget) {
      Array.from(this.recipientGroupsSelectTarget.selectedOptions).forEach(opt => {
        addHidden(`group:${opt.value}`)
      })
      this.recipientGroupsSelectTarget.disabled = true
    }

    // Role selections → "role:<role_key>"
    if (this.hasRecipientRolesSelectTarget) {
      Array.from(this.recipientRolesSelectTarget.selectedOptions).forEach(opt => {
        addHidden(`role:${opt.value}`)
      })
      this.recipientRolesSelectTarget.disabled = true
    }

    // Free-text email addresses — split by newline/comma/semicolon
    if (this.hasRecipientsInputTarget) {
      const raw = this.recipientsInputTarget.value || ""
      raw.split(/[\n,;]+/).map(s => s.trim()).filter(s => s.includes("@")).forEach(email => {
        addHidden(email)
      })
      // Rename the textarea so it doesn't submit on its own
      this.recipientsInputTarget.name = ""
    }
  }

  // Toggle email/drive config based on output type selection
  toggleOutputConfig(event) {
    const type = event.target.value
    if (this.hasEmailConfigTarget) {
      this.emailConfigTarget.classList.toggle("visually-hidden", !["email", "all"].includes(type))
    }
    if (this.hasDriveConfigTarget) {
      this.driveConfigTarget.classList.toggle("visually-hidden", !["drive_file", "all"].includes(type))
    }
  }

  // Open the drive folder picker modal, reset navigation stack, fetch root folders
  openFolderPicker(event) {
    event.preventDefault()

    this._folderNavStack = []

    const modal = new Modal(document.getElementById("driveFolderPickerModal"))
    modal.show()

    this._loadFolders(null)
  }

  // Navigate into a subfolder (called by "Open" button in each row)
  navigateIntoFolder(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const folderId   = btn.dataset.folderId
    const folderName = btn.dataset.folderName

    this._folderNavStack.push({ id: folderId, name: folderName })
    this._loadFolders(folderId)
  }

  // Navigate one level up (back button)
  navigateBack(event) {
    event.preventDefault()
    this._folderNavStack.pop()
    const parent = this._folderNavStack[this._folderNavStack.length - 1]
    this._loadFolders(parent ? parent.id : null)
  }

  // Load folders for the given parentId (null = root)
  _loadFolders(parentId) {
    this._renderNavBar()

    if (this.hasFolderPickerListTarget) {
      this.folderPickerListTarget.innerHTML = `
        <div class="text-center py-3">
          <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
        </div>`
    }

    const url = parentId
      ? `/drive/folders.json?parent_id=${encodeURIComponent(parentId)}`
      : "/drive/folders.json"

    fetch(url, {
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Accept": "application/json",
      },
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(folders => this._renderFolderList(folders))
      .catch(() => {
        if (this.hasFolderPickerListTarget) {
          const errorText = this.hasFolderPickerErrorTextValue
            ? this.folderPickerErrorTextValue
            : "Nem sikerült betölteni a mappákat."
          this.folderPickerListTarget.innerHTML = `
            <div class="alert alert-danger m-2">
              <i class="bi bi-exclamation-triangle me-2"></i>${errorText}
            </div>`
        }
      })
  }

  // Render the breadcrumb navigation bar inside the modal
  _renderNavBar() {
    if (!this.hasFolderPickerNavTarget) return

    const backLabel = this.hasFolderPickerBackLabelValue
      ? this.folderPickerBackLabelValue
      : "Vissza"
    const rootLabel = this.hasFolderPickerRootLabelValue
      ? this.folderPickerRootLabelValue
      : "Gyökér"

    if (this._folderNavStack.length === 0) {
      this.folderPickerNavTarget.innerHTML = ""
      return
    }

    // Build breadcrumb: root > folder1 > folder2 > …
    const crumbs = [
      `<button type="button"
               class="btn btn-sm btn-link p-0 text-decoration-none text-muted"
               data-action="click->workflow#navigateBack">
         <i class="bi bi-arrow-left me-1"></i>${backLabel}
       </button>`,
      `<span class="text-muted small mx-1">/</span>`,
      `<span class="small text-muted">${rootLabel}</span>`,
    ]

    this._folderNavStack.forEach(segment => {
      const escapedName = segment.name.replace(/</g, "&lt;")
      crumbs.push(`<span class="text-muted small mx-1">/</span>`)
      crumbs.push(`<span class="small fw-medium">${escapedName}</span>`)
    })

    this.folderPickerNavTarget.innerHTML =
      `<div class="d-flex align-items-center gap-1 flex-wrap px-2 py-1 border-bottom bg-light">
         ${crumbs.join("")}
       </div>`
  }

  // Render loaded folders as a list-group inside the modal
  _renderFolderList(folders) {
    if (!this.hasFolderPickerListTarget) return

    const noFoldersText = this.hasFolderPickerNoFoldersTextValue
      ? this.folderPickerNoFoldersTextValue
      : "Nincsenek mappák."
    const openLabel = this.hasFolderPickerOpenLabelValue
      ? this.folderPickerOpenLabelValue
      : "Megnyitás"
    const selectLabel = this.hasFolderPickerSelectLabelValue
      ? this.folderPickerSelectLabelValue
      : "Kiválaszt"

    if (folders.length === 0) {
      this.folderPickerListTarget.innerHTML = `
        <p class="text-muted text-center py-3 small">${noFoldersText}</p>`
      return
    }

    const items = folders.map(f => {
      const escapedPath = f.full_path.replace(/"/g, "&quot;").replace(/</g, "&lt;")
      const escapedName = f.name.replace(/</g, "&lt;")
      const openBtn = f.has_children
        ? `<button type="button"
                   class="btn btn-sm btn-outline-secondary flex-shrink-0"
                   title="${openLabel}"
                   data-folder-id="${f.id}"
                   data-folder-name="${escapedPath}"
                   data-action="click->workflow#navigateIntoFolder">
             <i class="bi bi-folder2-open"></i>
           </button>`
        : `<span class="flex-shrink-0" style="width:2rem"></span>`

      return `<div class="list-group-item d-flex align-items-center gap-2 py-2">
        <i class="bi bi-folder-fill text-warning flex-shrink-0"></i>
        <span class="text-truncate flex-grow-1">${escapedName}</span>
        <small class="text-muted text-truncate flex-shrink-0 d-none d-sm-block" style="max-width:35%">${escapedPath}</small>
        ${openBtn}
        <button type="button"
                class="btn btn-sm btn-primary flex-shrink-0"
                data-folder-id="${f.id}"
                data-folder-name="${escapedPath}"
                data-action="click->workflow#selectFolder">
          ${selectLabel}
        </button>
      </div>`
    }).join("")

    this.folderPickerListTarget.innerHTML =
      `<div class="list-group list-group-flush">${items}</div>`
  }

  // Handle folder selection from the picker modal
  selectFolder(event) {
    const btn = event.currentTarget
    const folderId   = btn.dataset.folderId
    const folderName = btn.dataset.folderName

    if (this.hasDriveFolderDisplayTarget) {
      this.driveFolderDisplayTarget.value = folderName
    }
    if (this.hasDriveFolderIdInputTarget) {
      this.driveFolderIdInputTarget.value = folderId
    }

    const modalEl = document.getElementById("driveFolderPickerModal")
    const bsModal = Modal.getInstance(modalEl)
    bsModal?.hide()
  }

  // Add a new step row via <template> clone
  addStep(event) {
    event.preventDefault()
    if (!this.hasStepsContainerTarget) return

    this.stepsContainerTarget.appendChild(this._buildStepRow(this._stepIndex))
    this._stepIndex++
    this._updatePositions()
  }

  // Mark a step row for deletion.
  // Persisted records: set _destroy=1 and hide the row (Rails soft-delete via nested attributes).
  // Unsaved new records: remove from DOM entirely so their required fields don't trigger
  // HTML5 validation and their blank attributes don't cause server-side validation errors.
  removeStep(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-workflow-target~='stepRow']")
    if (!row) return

    const idField = row.querySelector("input[type='hidden'][name$='[id]']")
    const destroyField = row.querySelector("[data-workflow-target~='stepDestroy']")

    if (idField?.value) {
      // Persisted record — soft delete via _destroy; Rails will destroy on save
      if (destroyField) destroyField.value = "1"
      row.style.display = "none"
    } else {
      // Unsaved new record — remove from DOM entirely
      row.remove()
    }
    this._updatePositions()
  }

  _buildStepRow(index) {
    const template = this.stepTemplateTarget.content.cloneNode(true)
    const prefix = `ai_workflow[steps_attributes][${index}]`
    // querySelector is intentional here: operating on a cloned DocumentFragment, not the controller scope
    template.querySelectorAll("[name^='WORKFLOW_STEP_NAME_PREFIX']").forEach(el => {
      el.name = el.name.replace("WORKFLOW_STEP_NAME_PREFIX", prefix)
    })
    const posField = template.querySelector("[data-workflow-target~='stepPosition']")
    if (posField) posField.value = index
    return template
  }

  _updatePositions() {
    this.stepRowTargets
      .filter(row => row.style.display !== "none")
      .forEach((row, i) => {
        const posField = row.querySelector("[data-workflow-target~='stepPosition']")
        if (posField) posField.value = i
      })
  }
}
